<template>
  <div class="autocomplete__wrapper">
    <div v-if="hasTestCustomers" class="mb-3">
      <base-text
        uppercase size="s" medium
        class="autocomplete__title"
      >
        Test
      </base-text>
      <div
        v-for="(customer, index) in testCustomers" :key="`ti-my-customer-cell-${index}`" class="autocomplete__item"
        :class="{'autocomplete__item--selected': isSelected(customer)}"
        @click="onItemClicked(customer)"
      >
        <base-avatar
          sans-serif background-color="#646464" inverse
          :size="32"
          :username="name(customer)"
        />
        <div>
          <base-text size="m" medium>
            {{ name(customer) }}
          </base-text>
          <base-text muted size="s" class="item--email">
            {{ customer.email }}
          </base-text>
        </div>
      </div>
    </div>
    <template v-if="!hideGroups & !myGroups.length == 0">
      <base-text
        uppercase size="s" medium
        class="autocomplete__title"
      >
        Groups
      </base-text>
      <div
        v-for="(group, index) in myGroups" :key="`ti-my-group-cell-${index}`" class="autocomplete__item"
        :class="{'autocomplete__item--selected': isSelected(group)}"
        @click="onItemClicked(group)"
      >
        <base-avatar
          :size="32"
          inverse
          :img="avatar"
        />
        <div>
          <base-text size="m" medium>
            {{ group.name }} ({{ group.member_count }})
          </base-text>
        </div>
      </div>
    </template>
    <div v-if="!myCustomers.length == 0" class="mb-3">
      <base-text
        uppercase size="s" medium
        class="autocomplete__title"
      >
        My clients
      </base-text>
      <div
        v-for="(customer, index) in myCustomers"
        :key="`ti-my-customer-cell-${index}`"
        class="autocomplete__item"
        :class="{'autocomplete__item--selected': isSelected(customer)}"
        :style="`${disabled(customer) ? `cursor: no-drop` : '' }`"
        @click="onItemClicked(customer)"
      >
        <base-avatar
          v-if="disabled(unsubscribed)"
          sans-serif background-color="#F2CEB1"
          :size="32"
          :username="name(customer)"
        />
        <base-avatar
          v-else
          sans-serif background-color="#E9AD7D"
          :size="32"
          :username="name(customer)"
        />
        <div>
          <base-text
            size="m"
            medium
            :style="`${disabled(customer) ? `color: rgba(51, 51, 51, 0.5)` : '' }`"
          >
            {{ longName(name(customer)) }}
          </base-text>
          <base-text
            muted size="s"
            class="item--email"
            :style="`${disabled(customer) ? 'color: rgba(51, 51, 51, 0.5)' : '' }`"
          >
            {{ customer.email }}
          </base-text>
        </div>
        <div style="margin-left:auto">
          <base-text v-if="unsubscribed(customer)" class="client-cell">
            Opted-Out
          </base-text>
        </div>
      </div>
    </div>
    <template v-if="!onlyMine & !otherCustomers.length == 0">
      <!-- <hr class="mt-0"> -->
      <base-text
        uppercase size="s" medium
        class="mt-2 autocomplete__title" bold
      >
        All clients
      </base-text>
      <div
        v-for="(customer, index) in otherCustomers" :key="`ti-other-customer-cell-${index}`" class="autocomplete__item"
        :class="{'autocomplete__item--selected': isSelected(customer)}"
        @click="onItemClicked(customer)"
      >
        <base-avatar
          class="avatar--outlined"
          sans-serif
          background-color="#D4D4D4"
          :size="32"
          :username="name(customer)"
        />
        <div>
          <base-text
            size="m"
            medium
            :style="`${disabled(customer) ? 'color: rgba(51, 51, 51, 0.5)' : '' }`"
          >
            {{ longName(name(customer)) }}
          </base-text>
          <base-text
            muted
            size="s"
            class="item--email"
            :style="`${disabled(customer) ? 'color: rgba(51, 51, 51, 0.5)' : '' }`"
          >
            {{ customer.email }}
          </base-text>
        </div>
        <div style="margin-left:auto">
          <base-text v-if="unsubscribed(customer)" class="client-cell">
            Opted-Out
          </base-text>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import _ from 'lodash';

export default {
  name: 'RecipientsSelect',
  props: {
    /** Serialized customers or groups. Can be mixed */
    items: {
      type: Array,
      default: () => [],
    },
    onlyMine: {
      type: Boolean,
      default: false,
    },
    hideGroups: {
      type: Boolean,
      default: false,
    },
    maxItems: {
      type: Number,
      default: 0,
    },
    selected: {
      type: Object,
      default: () => {},
    },
    communicationType: {
      type: String,
    },
  },
  computed: {
    avatar: {
      get() {
        if (this.items.is_supergroup) {
          return '/img/group_icon_black.png';
        } else {
          return '/img/group_icon_white.png';
        }
      },
    },
    myCustomers() {
      const result = this.items.filter((item) => item.is_mine && !item.is_group && item.pk >= 0);
      return this.maxItems > 0 ? result.splice(0, this.maxItems) : result;
    },
    otherCustomers() {
      const result = this.items.filter((item) => !item.is_mine && !item.is_group && item.pk >= 0);
      return this.maxItems > 0 ? result.splice(0, this.maxItems) : result;
    },
    myGroups() {
      const result = this.items.filter((item) => item.is_group && item.pk >= 0);
      return this.maxItems > 0 ? result.splice(0, this.maxItems) : result;
    },
    testCustomers() {
      return this.items.filter((item) => item.pk < 0 && !item.is_group && item.is_mine);
    },
    hasTestCustomers() {
      return this.testCustomers.length;
    },
  },
  methods: {
    longName(name) {
      if (!name) {
        return '';
      }

      if (name.length > 30) {
        return `${name.substring(0, 28)}...`;
      }
      return name;
    },
    isSelected(item) {
      return _.isEqual(this.selected, item);
    },
    name(customer) {
      if (customer.display_name_array) return customer.display_name_array[0];
      return customer.first_name ? `${customer.first_name} ${customer.last_name || ''}` : customer.external_id;
    },
    onItemClicked(item) {
      if (!this.unsubscribed(item)) {
        this.$emit('item-click', { item, index: this.getAbsoluteIndex(item) });
      }
    },
    getAbsoluteIndex(item) {
      return this.items.findIndex((i) => _.isEqual(i, item));
    },
    disabled(customer) {
      return (this.communicationType === 'email' && !customer.email) || this.unsubscribed(customer);
    },
    unsubscribed(customer) {
      /* eslint-disable default-case */
      switch (this.communicationType) {
        case 'email':
          return customer.email_unsubscribe;
        case 'embed':
          return customer.email_unsubscribe;
        case 'message':
          return customer.message_unsubscribe;
      }
      return undefined;
    },
  },
};
</script>
<style lang="scss" scoped>
$y-pad: 13px;
$x-pad: 23px;
.autocomplete {
  &__wrapper {
    padding: $y-pad 0 13px 0;
  }
  &__title {
    padding-left: $x-pad;
    padding-bottom: 3px;
    color: #AAAAAA;
    font-family: 'SilkaLight';
    font-style: normal;
    font-weight: 300;
    font-size: 14px;
    line-height: 17px;
    letter-spacing: 0.105px;
  }
  hr {
    padding: 0 $x-pad;
  }
  &__item {
    margin-left: 8px;
    margin-right: 8px;
    min-height: 44px;
    display: flex;
    align-items: center;
    column-gap: 16px;
    padding: 4px 16px 4px 16px;
    cursor: pointer;
    transition: all .2s ease;
    &:hover {
      background-color: #E2E2E2;
    }
    line-height: 14px;
    &--selected {
      background-color: #F0F0F0;
    }
  }
}
.avatar--outlined {
  // border: 1px solid $gray-light-2;
  border-radius: 50%;
  max-width: 30px !important;
  &.base-avatar .vue-avatar--wrapper {
    color: $gray-light-2 !important;
    background-color: transparent !important;
  }
}

.item--email {
  color: $gray-light-2 !important;
}

// unsub
.client-cell {
  font-weight: 300;
  font-size: 12px;
  color: rgba(51, 51, 51, 0.5);
}
</style>
